/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/
import { Card, IconX } from '@veneer/core';
import React, { memo, useMemo } from 'react';
import './styles.scss';
import { EmbeddedFeedbackContainerProps } from './types';
import Ratings from '../rating';
import Button from '../button';
import SelectInput from '../select-input';
import { withAnalytics } from '@veneer/analytics';
import { generateID } from '../../base/helper';

const TEmbeddedFeedbackContainer: React.FC<EmbeddedFeedbackContainerProps> = ({
  id = generateID('clientos-ui-toolkit-embedded-feedback-container'),
  title,
  variant = 'default',
  buttonText,
  onClose,
  actionButton,
  feedbackOption,
  ratings
}): JSX.Element => {
  const baseClass = 'clientos-ui-toolkit-embedded-feedback-container';

  const closeIcon = useMemo(() => {
    return (
      <div
        className={`${baseClass}__close`}
        role="button"
        onClick={onClose}
        id={`${id}__close`}
      >
        <IconX size={20} />
      </div>
    );
  }, [onClose, id]);

  const titleElement = useMemo(() => {
    return (
      <div
        className={`${baseClass}__header`}
        id={`${id}__header`}
      >
        <p
          className={`${baseClass}__title subtitle-regular--medium`}
          id={`${id}__title`}
        >
          {title}
        </p>
        {closeIcon}
      </div>
    );
  }, [closeIcon, title, id]);

  const variantRatings = useMemo(() => {
    return (
      <>
        {titleElement}
        <div
          className={`${baseClass}__content`}
          id={`${id}__content`}
        >
          <Ratings {...ratings} />
        </div>
      </>
    );
  }, [ratings, titleElement, id]);

  const variantOptions = useMemo(() => {
    return (
      <div
        className={`${baseClass}__options-container`}
        id={`${id}__options-container`}
      >
        {titleElement}
        <div
          className={`${baseClass}__options-content`}
          id={`${id}__options-content`}
        >
          <div
            className={`${baseClass}__select`}
            id={`${id}__select`}
          >
            <SelectInput {...feedbackOption} />
          </div>
          <div
            className={`${baseClass}__submit`}
            id={`${id}__submit`}
          >
            <Button
              id={`${id}__submit-button`}
              size="large"
              expanded
              disabled={feedbackOption?.value === undefined}
              {...actionButton}
            >
              {buttonText}
            </Button>
          </div>
        </div>
      </div>
    );
  }, [actionButton, buttonText, feedbackOption, titleElement, id]);

  const FeedbackContainer = useMemo(() => {
    switch (variant) {
      case 'ratings':
        return variantRatings;
      case 'options':
        return variantOptions;
      default:
        return titleElement;
    }
  }, [titleElement, variant, variantOptions, variantRatings]);

  return (
    <Card
      id={id}
      className={
        variant === 'ratings' ? `${baseClass} ratings` : `${baseClass}`
      }
      content={
        <div
          className={`${baseClass}__innerContainer`}
          id={`${id}__innerContainer`}
        >
          {FeedbackContainer}
        </div>
      }
    />
  );
};

export const EmbeddedFeedbackContainerAnalytics = withAnalytics(
  TEmbeddedFeedbackContainer
);
export default memo(TEmbeddedFeedbackContainer);
